import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ErrorModal from '../components/ErrorModal/ErrorModal';
import { setGlobalErrorHandler } from '../app/http-common';

/**
 * ErrorContext - Global error handling context
 * Provides showError() function to display error modals from anywhere in the app
 */
const ErrorContext = createContext(null);

/**
 * useError hook - Access the error context
 * @returns {{ showError: function, clearError: function }}
 */
export const useError = () => {
    const context = useContext(ErrorContext);
    if (!context) {
        throw new Error('useError must be used within an ErrorProvider');
    }
    return context;
};

/**
 * Parse an API error (Axios error) into a structured format
 * @param {Error} error - The error object (typically from Axios)
 * @returns {Object} Parsed error details
 */
export const parseApiError = (error) => {
    // Handle Axios errors
    if (error.response) {
        const { status, data, config } = error.response;
        return {
            statusCode: status,
            message: data?.message || data?.error || error.message || 'An unexpected error occurred',
            endpoint: config?.url,
            model: data?.model,
            errorId: data?.errorId
        };
    }
    
    // Handle network errors
    if (error.request) {
        return {
            statusCode: 0,
            message: 'Unable to connect to the server. Please check your internet connection.',
            endpoint: error.config?.url
        };
    }
    
    // Handle other errors
    return {
        message: error.message || 'An unexpected error occurred'
    };
};

/**
 * ErrorProvider - Wrap your app with this to enable global error handling
 */
export const ErrorProvider = ({ children }) => {
    const [errorState, setErrorState] = useState({
        isOpen: false,
        title: '',
        message: '',
        statusCode: null,
        model: null,
        endpoint: null,
        errorId: null,
        onRetry: null,
        retryLabel: 'Try Again'
    });

    /**
     * Show an error modal
     * @param {Object} options - Error options
     * @param {string} options.title - Modal title
     * @param {string} options.message - Error message
     * @param {number} [options.statusCode] - HTTP status code
     * @param {string} [options.model] - AI model name
     * @param {string} [options.endpoint] - API endpoint
     * @param {string} [options.errorId] - Error tracking ID
     * @param {function} [options.onRetry] - Retry callback
     * @param {string} [options.retryLabel] - Retry button label
     */
    const showError = useCallback((options) => {
        // If options is an Error object, parse it
        if (options instanceof Error) {
            const parsed = parseApiError(options);
            setErrorState({
                isOpen: true,
                title: 'Something went wrong',
                ...parsed,
                onRetry: null,
                retryLabel: 'Try Again'
            });
        } else {
            setErrorState({
                isOpen: true,
                title: options.title || 'Something went wrong',
                message: options.message || 'An unexpected error occurred',
                statusCode: options.statusCode || null,
                model: options.model || null,
                endpoint: options.endpoint || null,
                errorId: options.errorId || null,
                onRetry: options.onRetry || null,
                retryLabel: options.retryLabel || 'Try Again'
            });
        }
    }, []);

    // Register global error handler for axios interceptor
    useEffect(() => {
        const isFixture = new URLSearchParams(window.location.search).get('designFixture') === '1';
        if (!isFixture) {
            setGlobalErrorHandler(showError);
        }
        return () => setGlobalErrorHandler(null);
    }, [showError]);

    /**
     * Show an error from an API error (Axios error)
     * @param {Error} error - The Axios error
     * @param {Object} [options] - Additional options to override
     */
    const showApiError = useCallback((error, options = {}) => {
        const parsed = parseApiError(error);
        setErrorState({
            isOpen: true,
            title: options.title || 'Something went wrong',
            message: options.message || parsed.message,
            statusCode: parsed.statusCode,
            model: parsed.model || options.model,
            endpoint: parsed.endpoint,
            errorId: parsed.errorId,
            onRetry: options.onRetry || null,
            retryLabel: options.retryLabel || 'Try Again'
        });
    }, []);

    /**
     * Clear/close the error modal
     */
    const clearError = useCallback(() => {
        setErrorState(prev => ({ ...prev, isOpen: false }));
    }, []);

    const handleRetry = useCallback(() => {
        if (errorState.onRetry) {
            clearError();
            errorState.onRetry();
        }
    }, [errorState.onRetry, clearError]);

    return (
        <ErrorContext.Provider value={{ showError, showApiError, clearError }}>
            {children}
            <ErrorModal
                isOpen={errorState.isOpen}
                onClose={clearError}
                title={errorState.title}
                message={errorState.message}
                statusCode={errorState.statusCode}
                model={errorState.model}
                endpoint={errorState.endpoint}
                errorId={errorState.errorId}
                onRetry={errorState.onRetry ? handleRetry : null}
                retryLabel={errorState.retryLabel}
            />
        </ErrorContext.Provider>
    );
};

export default ErrorContext;
